import { IPurchaseState } from "@smartrr/shared/entities/PurchaseState";
import { ScheduledDeliveryGroupWithPurchSt } from "@smartrr/shared/entities/PurchaseState/scheduleUtils";
import { filterBundleParentsFromPurchasables } from "@smartrr/shared/utils/bundles";
import { shopifyGidToNumber } from "@smartrr/shared/utils/ensureShopifyGid";
import { isVariantInStock } from "@smartrr/shared/utils/variants";
import flatten from 'lodash/flatten';
import { useMemo } from "react";

import { useCleanedAndSortedPurchables } from "@customer-app/state/reducers/purchasables";
import {
  useEnablementMapForSellingPlanId,
  useTrendingSellingPlanGroup,
} from "@customer-app/state/reducers/sellingPlanGroups";
import { useSmartrrCustomerSelector } from "@customer-app/state/typedCustomerReduxHooks";

import { useCurrentBundle } from "../useCurrentBundle";

export const usePurchasablesForTrending = (delivery: ScheduledDeliveryGroupWithPurchSt<IPurchaseState>) => {
  const { purchaseState } = delivery;
  const { purchasables } = useCleanedAndSortedPurchables();
  const { bundle, allBundles, hideAllBundleItems, allBundleProductIds } = useCurrentBundle(delivery);
  const trendingConfig = useTrendingSellingPlanGroup();
  const enablementMaps = useEnablementMapForSellingPlanId(
    trendingConfig?.sellingPlans[0] ? trendingConfig?.sellingPlans[0].shopifyId : undefined
  );

  const sellingPlanGroupsByProductId = useSmartrrCustomerSelector(
    state => state.sellingPlanGroups.sellingPlanGroupsByProductId
  );
  const sellingPlanGroupsByVariantId = useSmartrrCustomerSelector(
    state => state.sellingPlanGroups.sellingPlanGroupsByVariantId
  );

  const hasBundle = useMemo(
    () => !!bundle || purchaseState.containsBundle,
    [purchaseState.containsBundle, bundle]
  );

  return useMemo(() => {
    if (!trendingConfig || !enablementMaps?.trendingListConfigWithEnablementMap) {
      return [];
    }

    const shopifyProductIdsSet = new Set<number>(
      Object.keys(enablementMaps.sellingPlanGroupWithEnablementMap?.enablementMap || {})
        .concat(Object.keys(enablementMaps.trendingListConfigWithEnablementMap?.enablementMap || {}))
        .map(shopifyGidToNumber)
    );

    return purchasables
      .filter(
        purchasable =>
          purchasable.shopifyId && shopifyProductIdsSet.has(shopifyGidToNumber(purchasable.shopifyId))
      )
      .filter(purchasable => {
        if (
          !purchasable.shopifyId ||
          !purchasable.isActiveInShopify ||
          !purchasable.vnts?.filter(vnt => vnt.isActiveInShopify).length ||
          purchasable.isDraftOrArchived
        ) {
          return false;
        }

        if (filterBundleParentsFromPurchasables(allBundles, purchasable)) {
          return false;
        }

        if ((hasBundle || hideAllBundleItems) && allBundleProductIds.includes(purchasable.shopifyId)) {
          return false;
        }

        const areAnyVariantsInStock = purchasable.vnts?.find(vnt => isVariantInStock(vnt));
        if (!areAnyVariantsInStock) {
          return false;
        }

        const productSellingPlanGroups = sellingPlanGroupsByProductId[shopifyGidToNumber(purchasable.shopifyId)];
        const variantSellingPlanGroups = flatten(
          purchasable.vnts?.map(vnt =>
            vnt.shopifyId ? sellingPlanGroupsByVariantId[shopifyGidToNumber(vnt.shopifyId)] : undefined
          )
        );
        return (
          productSellingPlanGroups?.some(g => !!g.sellingPlans.length) ||
          variantSellingPlanGroups.some(g => g && !!g.sellingPlans.length)
        );
      });
  }, [
    trendingConfig,
    enablementMaps,
    purchasables,
    allBundles,
    hasBundle,
    hideAllBundleItems,
    allBundleProductIds,
    sellingPlanGroupsByProductId,
    sellingPlanGroupsByVariantId,
  ]);
};
